import React, { useEffect, useState, useRef } from 'react';
import jsQR from 'jsqr';
import { Camera, CameraOff, X, Loader2 } from 'lucide-react';
interface QrScannerProps {
  /** Fired once per successful decode with the raw decoded text. */
  onResult: (text: string) => void;
  /** Optional close handler (renders an X button when provided). */
  onClose?: () => void;
}
/**
 * Live camera QR scanner.
 *
 * Decoding strategy (cross-browser, no native-only dependency):
 *   • Fast path — the native BarcodeDetector when available (Chromium/Android).
 *   • Fallback  — jsQR run over canvas frames, which works everywhere camera
 *                 access does, INCLUDING iOS Safari (where BarcodeDetector is
 *                 unavailable). This is why the admin can scan on an iPhone.
 *
 * It reads both kinds of codes the platform produces: a raw drop code, and a
 * QR that deep-links to a drop code / a customer's redemption code. The caller
 * (admin Verify tab) decides validity from the decoded text.
 *
 * Self-contained: handles permission errors, the "no camera" case, and always
 * releases the camera on unmount.
 */
export function QrScanner({
  onResult,
  onClose
}: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [status, setStatus] = useState<'starting' | 'scanning' | 'error'>('starting');
  const [errorMsg, setErrorMsg] = useState('');
  useEffect(() => {
    let cancelled = false;
    const BarcodeDetectorCtor: any = typeof window !== 'undefined' ? (window as any).BarcodeDetector : undefined;
    const stop = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
    let lastValue = '';
    let lastAt = 0;
    const emit = (value: string) => {
      const v = (value || '').trim();
      if (!v) return;
      const now = Date.now();
      // De-dupe rapid repeats of the same code (~1.5s window).
      if (v !== lastValue || now - lastAt > 1500) {
        lastValue = v;
        lastAt = now;
        onResultRef.current(v);
      }
    };
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('error');
        setErrorMsg('This browser cannot access the camera. Use the drop code field instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment'
          },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true'); // required for iOS Safari
        await video.play().catch(() => {});
        setStatus('scanning');
        const detector = BarcodeDetectorCtor ? new BarcodeDetectorCtor({
          formats: ['qr_code']
        }) : null;
        const tick = async () => {
          if (cancelled) return;
          const v = videoRef.current;
          if (v && v.readyState >= 2) {
            try {
              if (detector) {
                // Native fast path.
                const codes = await detector.detect(v);
                if (codes && codes.length > 0) emit(String(codes[0].rawValue));
              } else {
                // jsQR fallback (iOS Safari, etc.).
                const canvas = canvasRef.current;
                if (canvas) {
                  const w = v.videoWidth;
                  const h = v.videoHeight;
                  if (w && h) {
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d', {
                      willReadFrequently: true
                    } as any);
                    if (ctx) {
                      ctx.drawImage(v, 0, 0, w, h);
                      const img = ctx.getImageData(0, 0, w, h);
                      const result = jsQR(img.data, w, h, {
                        inversionAttempts: 'dontInvert'
                      });
                      if (result?.data) emit(result.data);
                    }
                  }
                }
              }
            } catch {
              /* transient decode errors are ignored; keep scanning */
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (err: any) {
        if (cancelled) return;
        setStatus('error');
        const name = err?.name || '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setErrorMsg('Camera access was blocked. Allow camera permission for this site (and make sure the page is on https), then try again.');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          setErrorMsg('No camera was found on this device.');
        } else {
          setErrorMsg('Could not start the camera. Make sure no other app is using it.');
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, []);
  return <div className="relative w-full" data-id="element-428" data-mp="2d7utv7lo02eh">
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black border border-gray-800" data-id="element-429" data-mp="2d8wi6droyle5">
        {/* Video preview */}
        <video ref={videoRef} className="h-full w-full object-cover" muted playsInline data-id="element-430" data-mp="2n6ivwwx986wj" />
        {/* Hidden canvas used for the jsQR fallback path */}
        <canvas ref={canvasRef} className="hidden" data-id="element-431" data-mp="2z8ts6pma991k" />

        {/* Scan reticle overlay */}
        {status === 'scanning' && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-id="element-432" data-mp="347n78mh7x83u">
            <div className="relative h-44 w-44" data-id="element-433" data-mp="38ywvpjr165e5">
              <span className="absolute left-0 top-0 h-7 w-7 rounded-tl-lg border-l-4 border-t-4 border-brand-yellow" data-id="element-434" data-mp="390g992lay2c8" />
              <span className="absolute right-0 top-0 h-7 w-7 rounded-tr-lg border-r-4 border-t-4 border-brand-yellow" data-id="element-435" data-mp="3hz1oanrkez89" />
              <span className="absolute bottom-0 left-0 h-7 w-7 rounded-bl-lg border-b-4 border-l-4 border-brand-yellow" data-id="element-436" data-mp="3my4tc87jp5g8" />
              <span className="absolute bottom-0 right-0 h-7 w-7 rounded-br-lg border-b-4 border-r-4 border-brand-yellow" data-id="element-437" data-mp="3rw6fj209pkvx" />
            </div>
          </div>}

        {/* Starting state */}
        {status === 'starting' && <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300" data-id="element-438" data-mp="1xlb4lt1m1tpz">
            <Loader2 className="mb-2 h-7 w-7 animate-spin text-brand-yellow" data-id="element-439" data-mp="1q1s60efvvrwa" />
            <p className="text-sm" data-id="element-440" data-mp="1l7xnvffxfkzb">Starting camera…</p>
          </div>}

        {/* Error state */}
        {status === 'error' && <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-gray-300" data-id="element-441" data-mp="1bbkuyjs7hb5m">
            <CameraOff className="mb-3 h-8 w-8 text-red-400" data-id="element-442" data-mp="28z2zfieu3r3t" />
            <p className="text-sm leading-relaxed" data-id="element-443" data-mp="23zqg37n6925z">{errorMsg}</p>
          </div>}

        {onClose && <button type="button" onClick={onClose} aria-label="Close scanner" className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-black/80" data-id="element-444" data-mp="1z0dlg0q26qsu">
            <X className="h-5 w-5" data-id="element-445" data-mp="nn1jjvdv0i9b" />
          </button>}
      </div>

      <p className="mt-3 flex items-center justify-center text-xs text-gray-500" data-id="element-446" data-mp="ilc84emq62kp">
        <Camera className="mr-1.5 h-3.5 w-3.5" data-id="element-447" data-mp="189zrattuuzgf" />
        {status === 'scanning' ? 'Point the camera at a QR or drop code' : status === 'error' ? 'Camera unavailable' : 'Requesting camera…'}
      </p>
    </div>;
}